import { useState, type FormEvent } from 'react'

import { Eye, EyeOff, Lock, Mail } from 'lucide-react'

import dotTile from '@/assets/illustrations/dot-tile.svg'
import loginHero from '@/assets/illustrations/login-hero.svg'
import { BrandIcon } from '@/components/BrandIcon'
import { Button, IconButton } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { TextField } from '@/components/ui/Field'
import { ErrorBanner } from '@/components/ui/states'
import { useToast } from '@/components/ui/toast-context'
import { errorMessage } from '@/lib/errors'
import { sendPasswordReset, signIn } from '@/services/auth'

export function LoginPage() {
  const toast = useToast()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (!email.trim()) return setError('Введите email')
    if (!password) return setError('Введите пароль')

    setLoading(true)
    setError(null)
    try {
      await signIn(email, password)
      // Дальше экран сменит AuthProvider: сюда управление уже не вернётся.
    } catch (caught) {
      setError(errorMessage(caught, 'Не удалось войти'))
      setLoading(false)
    }
  }

  async function onResetPassword() {
    if (!email.trim()) {
      setError('Введите email для сброса пароля')
      return
    }
    try {
      await sendPasswordReset(email)
      toast.success('Письмо для сброса пароля отправлено')
    } catch (caught) {
      setError(errorMessage(caught))
    }
  }

  const form = (
    <Card className="w-full max-w-[420px] animate-fade-up p-7 shadow-card-hover">
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-ink">Вход в админку</h1>
          <p className="mt-2 text-sm text-ink-muted">Только для администраторов KHANQ</p>
        </div>

        {error ? <ErrorBanner message={error} /> : null}

        <TextField
          label="Email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          leading={<Mail size={18} />}
        />

        <TextField
          label="Пароль"
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          leading={<Lock size={18} />}
          trailing={
            <IconButton
              label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
              onClick={() => setShowPassword((value) => !value)}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </IconButton>
          }
        />

        <Button type="submit" loading={loading} className="mt-2 w-full">
          Войти
        </Button>

        <Button type="button" variant="text" disabled={loading} onClick={onResetPassword} className="w-full">
          Забыли пароль?
        </Button>
      </form>
    </Card>
  )

  return (
    <div className="flex h-full">
      {/* Бренд-панель только на широком экране: на телефоне она съедала бы
          весь первый экран, оставляя форму под сгибом. */}
      <div
        className="hidden flex-1 flex-col justify-center bg-sidebar p-12 lg:flex"
        style={{ backgroundImage: `url(${dotTile})`, backgroundRepeat: 'repeat' }}
      >
        <div className="animate-fade-up">
          <BrandIcon size={56} radius={16} className="shadow-brand" />
          <p className="mt-6 text-4xl font-extrabold text-white">KHANQ Admin</p>
          <p className="mt-3 max-w-md text-base leading-relaxed whitespace-pre-line text-sidebar-text">
            {'Управление категориями и настройками\nдля мобильного приложения KHANQ'}
          </p>
        </div>

        {/* Картинка объясняет, чем панель занята: чек превращается в цифры.
            На узком экране её нет вовсе — там дорог каждый первый экран. */}
        <img
          src={loginHero}
          alt=""
          width={420}
          height={340}
          className="mt-10 w-full max-w-[420px] animate-fade-up"
          style={{ animationDelay: '120ms' }}
        />
      </div>

      <div className="flex flex-1 items-center justify-center overflow-y-auto p-6">
        <div className="flex w-full max-w-[420px] flex-col items-center">
          <div className="mb-8 flex animate-fade-up flex-col items-center lg:hidden">
            <BrandIcon size={64} radius={18} className="shadow-brand" />
            <p className="mt-4 text-2xl font-extrabold text-ink">KHANQ Admin</p>
          </div>
          {form}
        </div>
      </div>
    </div>
  )
}
